/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { useThemeObserver } from '@atlaskit/tokens';

import type { ProductKey } from '../../../types';

import backgroundDark from './assets/background-dark.svg';
import backgroundLight from './assets/background-light.svg';
import { FeatureNameColumn } from './feature-name-column';
import { FloatingRecommendation } from './floating-recommendation';
import { PremiumEditionColumn } from './premium-edition-column';
import { StandardEditionColumn } from './standard-edition-column';

const outerStyles = cssMap({
	root: {
		overflow: 'hidden',
	},
});

const panelStyles = cssMap({
	root: {
		width: '484px',
		height: '536px',
		position: 'absolute',
	},
});

const tableStyles = unboundedCssMap({
	root: {
		top: '120px',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const PanelStyleWrapper = ({ colorMode, children }: { children: any; colorMode: any }) => (
	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop */
	<Box
		xcss={panelStyles.root}
		style={
			colorMode === 'dark'
				? { background: `url(${backgroundDark}) no-repeat` }
				: { background: `url(${backgroundLight}) no-repeat` }
		}
	>
		{children}
	</Box>
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop */
);

/**
 * This is the right side panel depicted here:
 * https://www.figma.com/design/gN2uHK0wPD93kkqExPlNeM/Confluence%3A-Recommend-Standard-to-RT-trialing-customers-(RT)?node-id=3730-11924&node-type=frame&t=ZS7oExyVxcrlRkJQ-0
 */
export const FeatureComparisonPanel = ({
	productKey,
	standardPrice,
	premiumPrice,
}: {
	premiumPrice: number;
	productKey: ProductKey;
	standardPrice: number;
}): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const isConfluence = productKey === 'Confluence';

	return (
		<Flex xcss={outerStyles.root}>
			<FloatingRecommendation />
			<PanelStyleWrapper colorMode={colorMode}>
				<div css={tableStyles.root}>
					<FeatureNameColumn isConfluence={isConfluence} />
					<StandardEditionColumn standardPrice={standardPrice} />
					<PremiumEditionColumn premiumPrice={premiumPrice} />
				</div>
			</PanelStyleWrapper>
		</Flex>
	);
};
